import moment from "moment";

// ENG-1826: each CP agreement field's value on the two signing pages, and its label in the editor; unknown names are typed.

export const CP_KIND = Object.freeze({
  signature: "signature",
  name: "name",
  signingDate: "signingDate",
  licenseNumber: "licenseNumber",
  licenseExpiry: "licenseExpiry",
  deaNumber: "deaNumber",
  specialty: "specialty",
  email: "email",
  phone: "phone",
  address: "address",
  other: "other",
});

export const CP_LABEL = Object.freeze({ auto: "Filled automatically", signer: "Signer fills in" });

const KIND_BY_NAME = {
  name: CP_KIND.name,
  "full name": CP_KIND.name,
  date: CP_KIND.signingDate,
  "license number": CP_KIND.licenseNumber,
  "md license number": CP_KIND.licenseNumber,
  "expiration date": CP_KIND.licenseExpiry,
  "dea number": CP_KIND.deaNumber,
  specialty: CP_KIND.specialty,
  // The British spelling.
  speciality: CP_KIND.specialty,
  email: CP_KIND.email,
  phone: CP_KIND.phone,
  "phone number": CP_KIND.phone,
  address: CP_KIND.address,
};

export const normalizeFieldName = (name) => String(name ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export function classifyCpField(field) {
  if (field?.field_type === "signature") return CP_KIND.signature;
  return KIND_BY_NAME[normalizeFieldName(field?.field_name)] || CP_KIND.other;
}

const INPUT_BY_KIND = {
  [CP_KIND.name]: "text",
  [CP_KIND.signingDate]: "date",
  [CP_KIND.licenseNumber]: "text",
  [CP_KIND.licenseExpiry]: "date",
  [CP_KIND.deaNumber]: "text",
  [CP_KIND.email]: "email",
  [CP_KIND.phone]: "phone",
  [CP_KIND.address]: "textarea",
};
const INPUT_BY_TYPE = { date: "date", phone: "phone", gender: "gender", email: "email", number: "number" };

// A recognized field picks its own input: a number input blanks "TX-NP-1" (dev's license number fields are type number).
export function inputFor(kind, fieldType) {
  if (kind !== CP_KIND.other) return INPUT_BY_KIND[kind] || "text";
  return INPUT_BY_TYPE[fieldType] || "text";
}

export function formatPhone(raw) {
  const text = String(raw ?? "").trim();
  const digits = text.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : text;
}

const isoDateOf = (raw) => {
  const day = String(raw ?? "").slice(0, 10);
  return moment(day, "YYYY-MM-DD", true).isValid() ? day : "";
};

export function formatIsoDate(raw) {
  const day = isoDateOf(raw);
  return day ? moment(day, "YYYY-MM-DD", true).format("MM/DD/YYYY") : "";
}

const joinParts = (parts, separator) =>
  parts
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(separator);

// The hub's state ids start at 1 and the list at 0 (ENG-1880 was this off-by-one); the one place that offset lives.
export const stateAt = (stateId, stateList) => stateList[Number(stateId) - 1];

// Street and suite on one line, city, state and ZIP on the next.
export function formatOperatorAddress(details, stateList) {
  if (!details) return "";
  const street = joinParts([details.address_line_1, details.address_line_2], ", ");
  const place = joinParts([details.city, joinParts([stateAt(details.state, stateList), details.zip_code], " ")], ", ");
  return joinParts([street, place], "\n");
}

// An id the list lacks prints as itself, so a notice never reads "undefined".
export const stateNameOf = (stateId, stateList) => stateAt(stateId, stateList) || `State ${stateId}`;

const onFile = (value) => String(value ?? "").trim();

// Every current license in the state prints, in order; a repeat collapses to one, and a blank date is an em dash once there's more than one number to align under.
function licenseOnFile(licenses) {
  if (!Array.isArray(licenses)) return { blocked: "unavailable" };
  const isoByNumber = new Map();
  for (const license of licenses) {
    const number = onFile(license?.license_number);
    const iso = isoDateOf(license?.expiration_date);
    // sign-backend sends only current licenses, so a repeated number is a renewal: its latest date is the current one ("" sorts first, so a blank never wins).
    if (number && (!isoByNumber.has(number) || iso > isoByNumber.get(number))) isoByNumber.set(number, iso);
  }
  if (isoByNumber.size === 0) return { blocked: "missing" };
  const dates = [...isoByNumber.values()].map(formatIsoDate);
  const expiry = dates.length > 1 ? dates.map((date) => date || "—").join(", ") : dates.join(", ");
  return { number: [...isoByNumber.keys()].join(", "), expiry };
}

// The DEA is per state, not per license, so the same number repeats across a state's licenses: each one prints once.
function deaNumbersFrom(licenses) {
  if (!Array.isArray(licenses)) return "";
  return [...new Set(licenses.map((license) => onFile(license?.dea_number)).filter(Boolean))].join(", ");
}

// Phone and address are pre-filled from the record but stay editable; every other recognized field is locked.
const EDITABLE_KINDS = new Set([CP_KIND.phone, CP_KIND.address]);
const LICENSE_KINDS = new Set([CP_KIND.licenseNumber, CP_KIND.licenseExpiry]);

// context = cpFieldContext's; licenses is signer_licenses (null/undefined = unknown).
export function resolveCpField(field, context) {
  const kind = classifyCpField(field);
  const input = inputFor(kind, field?.field_type);
  // Unrecognized fields aren't in ENG-1826's table, so the signer still types them.
  if (kind === CP_KIND.other) return { kind, input, locked: false, value: "", prefill: "" };
  const licenses = context?.licenses;
  const license = licenseOnFile(licenses);
  const physician = context?.signer === "physician";
  const valueByKind = {
    [CP_KIND.name]: context?.name,
    [CP_KIND.signingDate]: context?.today,
    [CP_KIND.licenseNumber]: license.number,
    [CP_KIND.licenseExpiry]: license.expiry,
    [CP_KIND.deaNumber]: deaNumbersFrom(licenses),
    [CP_KIND.specialty]: context?.specialty,
    [CP_KIND.email]: context?.email,
    [CP_KIND.phone]: formatPhone(context?.phone),
    [CP_KIND.address]: physician ? "" : context?.address,
  };
  const value = onFile(valueByKind[kind]);
  if (EDITABLE_KINDS.has(kind)) {
    // A phone on file that isn't 10 digits would fight the input mask: the signer types it instead.
    const prefill = kind === CP_KIND.phone && !isTypedValueValid("phone", value) ? "" : value;
    return { kind, input, locked: false, value: "", prefill };
  }
  if (LICENSE_KINDS.has(kind) && license.blocked) return { kind, input, locked: true, value: "", prefill: "", blocked: license.blocked };
  return { kind, input, locked: true, value, prefill: "" };
}

// The old page placed the name by its index among text-type fields and today's date by its index among date fields.
export function legacyIndexes(fields) {
  let text = 0;
  let date = 0;
  return fields.map((field) => {
    const type = field?.field_type;
    if (type === "date") return { date: date++ };
    if (["signature", "gender", "phone"].includes(type)) return {};
    return { text: text++ };
  });
}

export const cpFieldKey = (field, index) => `cp_${field?.id ?? `n${index}`}`;

export function resolveCpFields(fields, context) {
  const legacy = legacyIndexes(fields);
  return fields
    .map((field, index) => ({ field, legacy: legacy[index], key: cpFieldKey(field, index) }))
    .filter(({ field }) => field?.field_type !== "signature")
    .map((entry) => ({ ...entry, ...resolveCpField(entry.field, context) }));
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isTypedValueValid(input, raw) {
  const value = String(raw ?? "").trim();
  if (value === "") return false;
  if (input === "date") return moment(value, "MM/DD/YYYY", true).isValid();
  if (input === "phone") return value.replace(/\D/g, "").length === 10;
  if (input === "email") return EMAIL.test(value);
  if (input === "number") return !Number.isNaN(Number(value));
  return true;
}

// An editable field holds what the signer typed, else its pre-filled value.
export const typedValue = (entry, typed) =>
  typed && Object.prototype.hasOwnProperty.call(typed, entry.key) ? String(typed[entry.key] ?? "") : entry.prefill ?? "";

const entryText = (entry, typed) => (entry.locked ? entry.value : typedValue(entry, typed).trim());

// A blocked license fails too, so Submit stays off even where a caller shows no notice.
export const allTypedFieldsValid = (resolved, typed) =>
  resolved.every((entry) => !entry.blocked && (entry.locked || isTypedValueValid(entry.input, typedValue(entry, typed))));

// Why the table holds an agreement; a record or lookup that didn't load says nothing about what's on file, so it comes first.
export function cpBlockedReason(resolved, context) {
  if (context?.detailsUnavailable) return "details-unavailable";
  if (resolved.some((entry) => entry.blocked === "unavailable")) return "unavailable";
  return resolved.some((entry) => entry.blocked === "missing") ? "missing" : null;
}

// Name and signing date keep the old offsets (AC5); pdf-lib's y is the baseline, running up from the page's bottom.
export function cpStampY(entry, pageHeight) {
  const { field, kind, legacy } = entry;
  const top = pageHeight - field.position_y;
  if (kind === CP_KIND.name && legacy?.text !== undefined) {
    return legacy.text === 0 ? top - field.height : top - 25;
  }
  if (kind === CP_KIND.signingDate && legacy?.date !== undefined) {
    if (legacy.date === 2) return top - 31;
    if (legacy.date === 1) return top - 24;
    return top - field.height;
  }
  return top - field.height;
}

// Helvetica's ascender and descender as shares of the font size, and the line spacing inside a fitted box.
const FIT = Object.freeze({ maxSize: 12, minSize: 6, step: 0.5, leading: 1.15, ascent: 0.72, descent: 0.22 });

// Each line of `text` wrapped to `width`; a word wider than the width breaks between characters. widthOf measures a
// string and must be additive (an unkerned measure is), so a long word is split in one pass.
export function wrapText(text, width, widthOf) {
  const lines = [];
  for (const paragraph of String(text ?? "").split(/\r?\n/)) {
    let line = "";
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const joined = line ? `${line} ${word}` : word;
      if (widthOf(joined) <= width) {
        line = joined;
        continue;
      }
      if (line) lines.push(line);
      let piece = "";
      let pieceWidth = 0;
      for (const character of Array.from(word)) {
        const characterWidth = widthOf(character);
        if (piece && pieceWidth + characterWidth > width) {
          lines.push(piece);
          piece = "";
          pieceWidth = 0;
        }
        piece += character;
        pieceWidth += characterWidth;
      }
      line = piece;
    }
    if (line) lines.push(line);
  }
  return lines;
}

// ENG-1826: a multi-line value prints inside its box, wrapped to the box's width and shrunk from 12pt (down to 6pt)
// until its lines fit the box's height. The lines stack up from the box's bottom edge.
export function fitTextInBox(text, box, widthOf) {
  const heightOf = (count, size) => (count - 1) * size * FIT.leading + (FIT.ascent + FIT.descent) * size;
  const linesAt = (size) => wrapText(text, box.width, (line) => widthOf(line, size));
  let size = FIT.maxSize;
  let lines = linesAt(size);
  while (heightOf(lines.length, size) > box.height && size - FIT.step >= FIT.minSize) {
    size -= FIT.step;
    lines = linesAt(size);
  }
  const lastBaseline = box.top - box.height + FIT.descent * size;
  return lines.map((line, index) => ({ text: line, x: box.x, y: lastBaseline + (lines.length - 1 - index) * size * FIT.leading, size }));
}

// The editor (SendTemplatePage) and the signing page render each page 600px wide, and a field's position and size are
// in those pixels. Every stamp starts 20 units in from the field's left edge.
export const EDITOR_PAGE_WIDTH = 600;
const STAMP_INSET = 20;

// The address box in PDF points: page width / 600 per pixel (1.02 on a Letter page). The other stamps keep reading
// pixels as points, as they always have (AC5).
function addressBox(field, page) {
  const scale = page.width / EDITOR_PAGE_WIDTH;
  const width = field.width * scale;
  return {
    x: field.position_x * scale + STAMP_INSET,
    top: page.height - field.position_y * scale,
    width: Math.max(width - STAMP_INSET, 1),
    height: field.height * scale,
  };
}

// One line at its old spot, shrunk from 12pt (down to 6pt) to the field's width: pdf-lib's maxWidth would wrap it down over the template's text.
function fitOnOneLine(text, width, widthOf) {
  let size = FIT.maxSize;
  while (widthOf(text, size) > width && size - FIT.step >= FIT.minSize) size -= FIT.step;
  return size;
}

// pages: [{ width, height }] in PDF points. widthOf(text, size) measures with the font the page stamps in.
export function cpStampPlan(resolved, typed, pages, widthOf) {
  return resolved.flatMap((entry) => {
    const { field } = entry;
    const pageIndex = field.page_number - 1;
    const page = pages[pageIndex];
    const text = entryText(entry, typed);
    if (page === undefined || text === "") return [];
    if (entry.kind === CP_KIND.address) {
      return fitTextInBox(text, addressBox(field, page), widthOf).map((line) => ({ pageIndex, ...line }));
    }
    return [{ pageIndex, text, x: field.position_x + STAMP_INSET, y: cpStampY(entry, page.height), size: fitOnOneLine(text, field.width, widthOf) }];
  });
}

const LICENSE_HINT =
  "From the signer's license on file for the agreement's state (every current one, when there are two). If none is on file, the agreement can't be signed until support adds it.";
const HINT_BY_KIND = {
  [CP_KIND.name]: "The signer's name on file.",
  [CP_KIND.signingDate]: "The date the agreement is signed.",
  [CP_KIND.licenseNumber]: LICENSE_HINT,
  [CP_KIND.licenseExpiry]: LICENSE_HINT,
  [CP_KIND.deaNumber]: "From the signer's DEA number on file for the agreement's state; blank when there's none.",
  [CP_KIND.specialty]: "From the signer's record when it's on file; blank until then.",
  [CP_KIND.email]: "From the signer's record.",
};

// A CP template's first signer is the physician and its second the operator (the signing pages' signingOrderFor).
export const cpSignerForOrder = (signingOrder) => (signingOrder === 1 ? "physician" : signingOrder === 2 ? "operator" : null);

// signer: the editor's selected signer ("physician", "operator" or null when unknown); only Address depends on it.
export function cpEditorLabel(standardField, signer = null) {
  const kind = classifyCpField(standardField);
  if (kind === CP_KIND.signature) return { text: CP_LABEL.signer, hint: "The signer signs here." };
  if (kind === CP_KIND.other) return { text: CP_LABEL.signer, hint: "The signer types it before submitting." };
  if (kind === CP_KIND.address && signer === "physician") return { text: CP_LABEL.signer, hint: "The physician types it: no address is on file." };
  if (kind === CP_KIND.phone) return { text: CP_LABEL.signer, hint: "Pre-filled from the signer's record when it's on file; the signer can change it." };
  if (kind === CP_KIND.address && signer === "operator") {
    return { text: CP_LABEL.signer, hint: "Pre-filled from the operator's record, suite included, when it's on file; the operator can change it." };
  }
  if (kind === CP_KIND.address) {
    return { text: CP_LABEL.signer, hint: "Operator: pre-filled from the record, suite included, and editable. Physician: typed." };
  }
  return { text: CP_LABEL.auto, hint: HINT_BY_KIND[kind] };
}

// The record values a CP page fills fields from; every CP signing page uses it.
export function cpFieldContext(signLink, { isCp }, today, stateList) {
  const physician = signLink?.collab_phys_details;
  const operator = signLink?.operator_details;
  return {
    signer: isCp ? "physician" : "operator",
    today,
    name: isCp ? physician?.name : [operator?.first_name, operator?.last_name].filter(Boolean).join(" "),
    email: isCp ? physician?.email : operator?.email,
    phone: isCp ? physician?.phone_number : operator?.phone_number,
    address: isCp ? "" : formatOperatorAddress(operator, stateList),
    licenses: signLink?.signer_licenses,
    specialty: isCp ? physician?.specialty : operator?.specialty,
    // sign-backend sends no physician, or an operator without a name or email, when it couldn't load the signer's record; a link that hasn't loaded says nothing yet.
    detailsUnavailable:
      Boolean(signLink) &&
      (isCp
        ? onFile(physician?.name) === ""
        : [operator?.first_name, operator?.last_name, operator?.email].every((part) => onFile(part) === "")),
  };
}
